import { useMemo } from 'react';

import { useJiraContext } from 'src/services/jira';
import { logout } from 'src/services/session';

import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import pd from 'src/lib/api/pagerduty';
import { bugsnagNotify } from 'src/lib/bugsnag';
import { createLazySingletonContext, usePromise } from 'src/lib/hooks';

const initValue = {
  loading: true,
  error: undefined,
  hasAtLeastOneService: undefined,
  services: []
};

const getServicesFromJiraService = async (projectId) => {
  const { services } = await integration.getAssociatedServices(projectId).catch(e => {
    if (e.message.startsWith('Unauthorized')) {
      logout();
    } else {
      bugsnagNotify(e);
    }
    return { services: [] };
  });
  return services.map(i => ({ ...i, summary: i.label, fromService: true }));
};

const useContextBody = () => {
  const { settings, context: { issueKey, issueTypeId } = {} } = useJiraContext();

  const { loading, data, error } = usePromise(async () => {
    if (!settings) return new Promise(() => {});

    const issue = await jira.getIssue(issueKey);
    const projectId = issue?.fields?.project?.id;
    const newIntegrationServices = await getServicesFromJiraService(projectId);

    const { services: oldIntegrationServices } = (settings && settings.v3AuthToken && settings.v3AuthToken.value)
      ? await pd.getLegacySidebarInfo(issueKey, issueTypeId)
      : { services: [] };
    const newIntegrationServicesIds = newIntegrationServices.map(i => i.id);
    return [
      ...newIntegrationServices,
      ...(oldIntegrationServices.filter(i => !newIntegrationServicesIds.includes(i.id)))
    ];
  }, [settings, issueKey, issueTypeId]);

  return useMemo(() => ({
    loading,
    error,
    services: data || [],
    hasAtLeastOneService: data && data.length > 0
  }), [loading, error, data]);
};

export const {
  Provider,
  getCurrentValue: getAssociatedServices,
  useHook: useAssociatedServices,
} = createLazySingletonContext(initValue, useContextBody);
